import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {
    OptionType,
    EecCategoryImpressionsType,
} from '@optimaxdev/analytics/desktop/types/ecommerce';
import {emptyByDefault} from '@optimaxdev/analytics/desktop/utils/emptyByDefault/emptyByDefault';
import {convertPriceEEC} from '@optimaxdev/utils';

import {BaseImpressionDataType, EecOptionsType} from 'desktop/types/analyticsTypes';
import {ExtendedProductType} from 'desktop/types/typesProduct';
import {getProductType} from 'libs/getProductType';
import {logWithSentryEEC} from 'libs/sentry/sentry';

import {RecommendationStrategyType} from 'types/recommendationStrategyType';

import {ProductDataType, EEC_RECOMMENDATION} from 'types/recommendationType';

import {findPriceWithDiscount, getBrandName, getFrameSize, getName, getVariant} from './helpers';

/**
 * Prepare basic fields.
 *
 * @param {ExtendedProductType} product - Product data.
 * @param {EecOptionsType} options - Options.
 * @returns {BaseImpressionDataType} - Basic fields.
 */
// eslint-disable-next-line complexity
const prepareBaseImpressionData = (
    product: ExtendedProductType,
    options: EecOptionsType = {},
): BaseImpressionDataType => {
    const sku =
        ('product_sku' in product && product.product_sku) ||
        ('sku' in product && product.sku) ||
        '';

    const category =
        ('category' in product && product.category) || options.category || 'Non category page';

    const price =
        options.price ||
        findPriceWithDiscount(product) ||
        ('price' in product && Number(product.price) >= 0 ? product.price : '');

    return {
        brand: getBrandName(product, options),
        category: emptyByDefault(category),
        id: emptyByDefault(sku),
        name: getName(product),
        price: convertPriceEEC(price),
        variant: getVariant(product, getFrameSize(product)),
    };
};

/**
 * Preparing impressions for sending an event to dataLayer
 * @param {Array<ProductDataType>} products - products list
 * @param {OptionType} options - general advanced options
 * @returns {Array<EecCategoryImpressionsType>} - prepared impressions for event dispatch
 */
export const prepareImpressionDataForRecommendationProducts = (
    products: ProductDataType[],
    options: OptionType,
): EecCategoryImpressionsType[] =>
    products.map(
        (product: ProductDataType, index: number): EecCategoryImpressionsType => ({
            ...prepareBaseImpressionData(product, {
                ...options,
                index,
            }),
            dimension36: convertPriceEEC(product.price),
            dimension42: '',
            dimension43: '',
            dimension56: options.category || product.currentLocation || '',
            dimension60: `Prescription ${getProductType(product.categories)}`,
            list: options.list || '',
            position: index + 1,
            product_id: product.product_id,
        }),
    );

const defaultOptions = {category: '', isQuizPDP: false, strategy: {}};

/**
 * Sends a list of viewed recommended products to the analytics.
 * @param {ProductDataType} productItems - product item
 */
export const sendAnalyticsForRecommendationProducts = (
    productItems: ProductDataType[],
    options: {
        category?: string;
        isQuizPDP?: boolean;
        strategy?: RecommendationStrategyType;
    } = defaultOptions,
) => {
    const {category, isQuizPDP, strategy} = Object.assign({}, defaultOptions, options);
    const impressions = prepareImpressionDataForRecommendationProducts(productItems, {
        category,
        list: window ? window.location.pathname : '',
    });

    const eventName = isQuizPDP
        ? 'EEC_Quiz_Result_VTO_Impressions'
        : EEC_RECOMMENDATION.VIEWED_IMPRESSION;
    const event = {
        ecommerce: {
            ...strategy,
            impressions,
        },
        event: eventName,
    };
    pushDataLayer(event);

    logWithSentryEEC(eventName, event);
};
